/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useState } from 'react'
import RegularTemplate from '@/components/templates/RegularTemplate'
import { samsBuildBatchesUrls } from '@/configs/urls'
import { useFetchItemsWithParamsV2 } from '@/hooks/fetch/useFetchItemsWithParamsV2'
import { useNavigate, useParams } from 'react-router-dom'
import HeaderBatchItem from '@/components/batches/HeaderBatchItem'
import { columns } from './handlers'
import MainTotalItems from '@/components/listings/MainTotalItems'
import DefaultCrudTable from '@/components/tables/DefaultCrudTable'
import TableBody from './components/TableBody'
import { Box, Button, useToast } from '@chakra-ui/react'
import DownloadFileButton from '@/components/buttons/DownloadFileButton'
import {
  getSamBuildBatch,
  getSamBuildBatchItemsAsCsv,
  getSamBuildBatchItemsByBatchId,
  retryProcessSam,
  retryProcessSamBuildBatch,
  startProcessSamBuildBatch
} from '@/services/sams-build-batches'
import {
  BACK_TO_SAMS_BUILD_BATCHES,
  BUILD_SAMS_BATCH_TITLE
} from '@/configs/strings/sams-build-batches.strings'
import { EXPORT_SAMS_LABEL } from '@/configs/strings/sams'
import BatchForm from './components/BatchForm'
import { generalBatchStates } from '@/configs/server-enums'
import { defaultToastConfig } from '@/configs/toast'
import { getFailedDescription, getOnErrorTitle, getSuccessDescription } from '@/configs/strings/batches'
import { useAuth0 } from '@auth0/auth0-react'
import { getBatchProcessInfo } from '@/utils/batches'
import RetryModal from './components/RetryModal'

const SamsBuildBatch = () => {
  const toast = useToast()
  const [retryProcess, setRetryProcess] = useState()
  const [retryModalIsOpen, setRetryModalIsOpen] = useState(false)
  const [retryBatch, setRetryBatch] = useState()
  const [samSelected, setSamSelected] = useState()
  const { processing } = generalBatchStates
  const batchType = 'SAM Build Batch'
  const { batchId } = useParams()
  const navigate = useNavigate()
  const {
    itemsResponse: samsBuildBatch,
    reloadItems
  } = useFetchItemsWithParamsV2({
    fetchService: getSamBuildBatch,
    fetchParams: { id: batchId }
  })
  const {
    itemsResponse,
    onChangePage,
    page,
    totalPages
  } = useFetchItemsWithParamsV2({
    fetchService: getSamBuildBatchItemsByBatchId,
    fetchParams: { id: batchId }
  })
  const [isLoading, setIsLoading] = useState(false)
  const { getAccessTokenSilently: getAccessToken } = useAuth0()
  const batchName = samsBuildBatch?.name
  const batchState = samsBuildBatch?.state
  const HeaderBatch = () => (
    <HeaderBatchItem
      title={batchName}
      batchState={batchState}
    />
  )
  const {
    batchHasErrors,
    isProcessing
  } = getBatchProcessInfo(samsBuildBatch?.state)
  const numberAssets = itemsResponse?.total
  const downloadCsv = (getAccessToken) => getSamBuildBatchItemsAsCsv({ id: batchId }, getAccessToken)
  const onStartProcess = async (data) => {
    setIsLoading(true)
    try {
      const listingBatchUpdated = await startProcessSamBuildBatch(
        { id: samsBuildBatch?.id, data },
        getAccessToken
      )

      if (listingBatchUpdated.state === processing) {
        toast({
          title: batchType,
          description: getSuccessDescription(
            batchType,
            listingBatchUpdated.name
          ),
          status: 'success',
          ...defaultToastConfig
        })
      }
    } catch (error) {
      toast({
        title: getOnErrorTitle(batchType),
        description: error.response?.data?.message || getFailedDescription(batchType),
        status: 'error',
        ...defaultToastConfig
      })
    } finally {
      navigate(`/${samsBuildBatchesUrls.base}`, { replace: true })

      setIsLoading(false)
    }
  }

  const onCloseRetryModal = () => {
    reloadItems()
    setRetryModalIsOpen(false)
    setSamSelected(null)
    setRetryBatch(false)
  }

  const onOpenRetryModal = (sam) => {
    setRetryProcess(() => retryProcessSam)
    setSamSelected(sam)
    setRetryModalIsOpen(true)
  }

  const onOpenRetryBatchModal = () => {
    setRetryProcess(() => retryProcessSamBuildBatch)
    setRetryModalIsOpen(true)
    setRetryBatch(true)
  }

  return (
    <RegularTemplate
      backLabel={BACK_TO_SAMS_BUILD_BATCHES}
      backUrl={`/${samsBuildBatchesUrls.base}`}
      childrenHeader={HeaderBatch}
      title={BUILD_SAMS_BATCH_TITLE}
      loadingContent={isLoading}
    >
      { retryModalIsOpen &&
        <RetryModal
          batchId={batchId}
          batchName={batchName}
          getAccessToken={getAccessToken}
          onClose={onCloseRetryModal}
          onRetry={retryProcess}
          retryBatch={retryBatch}
          sam={samSelected}
        />
      }
      <BatchForm
        processingCostDetails={samsBuildBatch?.processingCostDetails}
        batchState={batchState}
        sendData={onStartProcess}
      />
      <MainTotalItems
        label="SAMs in this batch"
        total={numberAssets}
      />
      <Box
        display="flex"
        justifyContent="space-between"
        className="mb-5 mt-5"
      >
        { batchHasErrors &&
          <Button
            onClick={onOpenRetryBatchModal}
          >
            Retry Batch
          </Button>
        }
        <DownloadFileButton
          service={downloadCsv}
          isDisabled={!itemsResponse?.data?.length}
          dataKey="csv"
        >
          { EXPORT_SAMS_LABEL }
        </DownloadFileButton>
      </Box>
        <DefaultCrudTable
          page={page}
          totalPages={totalPages}
          onPageChange={(page) => onChangePage(page)}
        >
          <DefaultCrudTable.Header columns={columns} />
          <TableBody
            columns={columns}
            items={itemsResponse?.data}
            onRetry={!isProcessing && onOpenRetryModal}
          />
        </DefaultCrudTable>
    </RegularTemplate>
  )
}

export default SamsBuildBatch
